import { Directive, Injectable, inject } from '@angular/core';

import { EMPTY, Subject } from 'rxjs';

import {
  AutoLinkWrapPlugin,
  BasicPlugin,
  BoldPlugin,
  ConfigPlugins,
  ContextMenuPlugin,
  EditorButton,
  EmojiEditorPlugin,
  IProsemirrorConfig,
  ItalicPlugin,
  LinkEditPlugin,
  ListPlugin,
  PluginsButtons,
  PluginsEvents,
  RtSchema,
  SCHEMAS,
  UnderlinePlugin,
} from '@core/prosemirror-legacy';
import { ProsemirrorBaseConfig, ScrollStream$ } from '@core/prosemirror-legacy/angular';
import { SymbolPickerService } from '@core/symbol-legacy';

import { ListMenuService } from '../components/list-menu/list-menu.service';
import { ToolbarService } from '../components/toolbar/toolbar.service';
import { ContextToolbarService } from '../components/tooltips/context-toolbar/context-toolbar.service';
import { LinkComponentService } from '../components/tooltips/link/link-component.service';
import { ProsemirrorConfig } from '../providers';

export type ProsemirrorOfferLetterConfigEvents = PluginsEvents<
  ConfigPlugins<ProsemirrorOfferLetterConfig>
>;

@Injectable()
export class ProsemirrorOfferLetterConfig
  extends ProsemirrorBaseConfig
  implements IProsemirrorConfig
{
  protected readonly symbolPickerService? = inject(SymbolPickerService, { optional: true });
  protected readonly toolbarService? = inject(ToolbarService, { optional: true });
  protected readonly contextMenuService? = inject(ContextToolbarService, { optional: true });
  protected readonly linkService? = inject(LinkComponentService, { optional: true });
  protected readonly scrollStream$? = inject(ScrollStream$, { optional: true });
  protected readonly listMenuService? = inject(ListMenuService, { optional: true });

  public eventStream$ = new Subject<ProsemirrorOfferLetterConfigEvents>();

  private scroll$ = this.scrollStream$ || EMPTY;

  public getSchema() {
    return SCHEMAS[RtSchema.Default](document);
  }

  public getRenderPlugins() {
    return [];
  }

  public getEditorPlugins() {
    return [
      new BasicPlugin(),
      new ContextMenuPlugin(this.contextMenuService!, this.scroll$),
      new EmojiEditorPlugin(this.symbolPickerService!, this.scroll$),

      new BoldPlugin(),
      new ItalicPlugin(),
      new UnderlinePlugin(),
      new LinkEditPlugin(this.scroll$, this.linkService!),
      new ListPlugin(this.listMenuService!),
      new AutoLinkWrapPlugin(),
    ];
  }

  public registerToolbarButtons(
    buttons: PluginsButtons<ConfigPlugins<ProsemirrorOfferLetterConfig>>,
  ) {
    const ctxButtons = [
      buttons.toggleBold,
      buttons.toggleItalic,
      buttons.toggleUnderline,
      buttons.toggleBulletList,
      buttons.toggleOrderedList,
      buttons.toggleLink,
    ];
    const leftButtons = [
      [
        buttons.toggleBold,
        buttons.toggleItalic,
        buttons.toggleUnderline,
        buttons.toggleBulletList,
        buttons.toggleOrderedList,
        buttons.toggleLink,
      ],
    ];

    const rightButtons: EditorButton[] = [buttons.toggleEmojiList];
    this.toolbarService!.setContextButtons(ctxButtons);
    this.toolbarService!.setLeftButtons(leftButtons);
    this.toolbarService!.setRightButtons(rightButtons);
  }
}

@Directive({
  selector: '[prosemirrorOfferLetterConfig]',
  providers: [
    ProsemirrorOfferLetterConfig,
    { provide: ProsemirrorConfig, useValue: ProsemirrorOfferLetterConfig },
  ],
  exportAs: 'prosemirrorConfig',
  standalone: true,
})
export class ProsemirrorOfferLetterConfigDirective {}
